import React, { lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle } from 'lucide-react';
const GridPattern = () => <div className="absolute inset-0 w-full h-full opacity-[0.03] pointer-events-none" style={{
  backgroundImage: 'linear-gradient(#F5F5F5 1px, transparent 1px), linear-gradient(90deg, #F5F5F5 1px, transparent 1px)',
  backgroundSize: '40px 40px'
}} aria-hidden="true" data-id="element-2009" />;
export function KidsDecode() {
  const navigate = useNavigate();
  const handleGenerateDiagnostic = () => {
    navigate('/start?product=child');
  };
  return <>
      <Helmet data-id="element-2010">
        <title data-id="element-2011">Child System Map | Lazy Monk Labs</title>
        <meta name="description" content="A structural map of your child's natural wiring. Understand how they process the world, where they are sensitive, and how pressure affects them." data-id="element-2012" />
        <meta name="robots" content="index, follow" data-id="element-2013" />
        <link rel="canonical" href="https://lazymonklabs.com/child-map" data-id="element-2014" />

        <script async src="https://www.googletagmanager.com/gtag/js?id=G-QCXDHW3F0P" data-id="element-2015"></script>
        <script data-id="element-2016">
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-QCXDHW3F0P');
          `}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-2017">
        {/* Navigation */}
        <header data-id="element-2018">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-2019">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-2020">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-2021">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" data-id="element-2022" />
                <span className="text-lg font-medium tracking-tight" data-id="element-2023">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-2024">
                <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-2025">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-2026">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16" data-id="element-2027">
          {/* Hero */}
          <section className="relative py-20 px-6 overflow-hidden" data-id="element-2028">
            <GridPattern data-id="element-2029" />
            <div className="relative z-10 max-w-4xl mx-auto" data-id="element-2030">
              <Link to="/reports" className="inline-flex items-center gap-2 text-sm text-[#F5F5F5]/60 hover:text-[#F5F5F5] transition-colors mb-8" data-id="element-2031">
                ← Back to Reports
              </Link>

              <h1 className="text-4xl md:text-5xl font-medium mb-6 leading-tight tracking-tight" data-id="element-2032">
                Child System Map
              </h1>
              <p className="text-xl text-[#F5F5F5]/70 mb-12 leading-relaxed" data-id="element-2033">
                A structural map of your child's natural wiring.
              </p>
            </div>
          </section>

          {/* What This Map Covers */}
          <section className="py-16 px-6 border-t border-[#2B2B2B]" data-id="element-2034">
            <div className="max-w-4xl mx-auto" data-id="element-2035">
              <h2 className="text-3xl font-medium mb-8" data-id="element-2036">
                What this map covers
              </h2>

              <div className="space-y-6" data-id="element-2037">
                {[{
                title: 'Core Temperament & Nervous System Type',
                desc: 'How this child processes stimulation, emotion, and pressure. Not personality — wiring.'
              }, {
                title: 'Sensitivity Zones',
                desc: 'Where this system feels pressure more strongly. What activates stress responses.'
              }, {
                title: 'Regulation Patterns',
                desc: 'How this child stabilizes themselves. What helps them reset and recover.'
              }, {
                title: 'Learning & Energy Style',
                desc: 'How this system learns best. Where energy flows naturally and where it drains.'
              }, {
                title: 'Environment Fit',
                desc: 'What environments support this wiring. What environments create overload.'
              }, {
                title: 'Developmental Pressure Points',
                desc: 'Key risk zones where this system is most vulnerable to misalignment.'
              }].map((item, i) => <div key={i} className="flex items-start gap-4 border-l-2 border-[#2B2B2B] pl-6" data-id="element-2038">
                    <CheckCircle2 className="w-5 h-5 text-[#64748B] flex-shrink-0 mt-1" data-id="element-2039" />
                    <div data-id="element-2040">
                      <h3 className="text-lg font-medium mb-2" data-id="element-2041">{item.title}</h3>
                      <p className="text-[#F5F5F5]/70" data-id="element-2042">{item.desc}</p>
                    </div>
                  </div>)}
              </div>
            </div>
          </section>

          {/* What This Is NOT */}
          <section className="py-16 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-2043">
            <div className="max-w-4xl mx-auto" data-id="element-2044">
              <h2 className="text-3xl font-medium mb-8" data-id="element-2045">What this is not</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2046">
                {['Not a diagnosis', 'Not parenting advice', 'Not an evaluation of intelligence', 'Not prediction of future outcomes', 'Not a fix or solution', 'Not judgment of parenting quality'].map((item, i) => <div key={i} className="flex items-center gap-3 p-4 border border-[#2B2B2B] bg-[#0A0A0A]" data-id="element-2047">
                    <XCircle className="w-5 h-5 text-[#64748B] flex-shrink-0" data-id="element-2048" />
                    <span className="text-[#F5F5F5]/70 text-sm" data-id="element-2049">{item}</span>
                  </div>)}
              </div>

              <p className="mt-8 text-[#F5F5F5]/70 leading-relaxed" data-id="element-2050">
                This map explains how a child's system is wired and how it
                operates. It does not tell you what to do or how to change them.
              </p>
            </div>
          </section>

          {/* How It Works */}
          <section className="py-16 px-6 border-t border-[#2B2B2B]" data-id="element-2051">
            <div className="max-w-4xl mx-auto" data-id="element-2052">
              <h2 className="text-3xl font-medium mb-12" data-id="element-2053">How it works</h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-2054">
                <div className="text-center" data-id="element-2055">
                  <div className="w-12 h-12 rounded-full bg-[#111214] border border-[#2B2B2B] flex items-center justify-center mx-auto mb-4 text-[#64748B] font-medium" data-id="element-2056">
                    1
                  </div>
                  <h3 className="text-lg font-medium mb-2" data-id="element-2057">
                    Enter child's birth data
                  </h3>
                  <p className="text-sm text-[#F5F5F5]/60" data-id="element-2058">
                    Date, time (if known), and place of birth
                  </p>
                </div>

                <div className="text-center" data-id="element-2059">
                  <div className="w-12 h-12 rounded-full bg-[#111214] border border-[#2B2B2B] flex items-center justify-center mx-auto mb-4 text-[#64748B] font-medium" data-id="element-2060">
                    2
                  </div>
                  <h3 className="text-lg font-medium mb-2" data-id="element-2061">
                    System generates map
                  </h3>
                  <p className="text-sm text-[#F5F5F5]/60" data-id="element-2062">
                    Analysis runs automatically
                  </p>
                </div>

                <div className="text-center" data-id="element-2063">
                  <div className="w-12 h-12 rounded-full bg-[#111214] border border-[#2B2B2B] flex items-center justify-center mx-auto mb-4 text-[#64748B] font-medium" data-id="element-2064">
                    3
                  </div>
                  <h3 className="text-lg font-medium mb-2" data-id="element-2065">
                    View preview → Unlock full
                  </h3>
                  <p className="text-sm text-[#F5F5F5]/60" data-id="element-2066">
                    See preview first, then unlock complete map
                  </p>
                </div>
              </div>

              <div className="border border-[#2B2B2B] p-8 bg-[#111214]" data-id="element-2067">
                <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2068">
                  <strong className="text-[#F5F5F5]" data-id="element-2069">No questionnaires.</strong>{' '}
                  No explaining your child. No subjective inputs. The system
                  uses structural data only.
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-20 px-6 relative overflow-hidden border-t border-[#2B2B2B]" data-id="element-2070">
            <GridPattern data-id="element-2071" />
            <div className="relative z-10 max-w-4xl mx-auto text-center" data-id="element-2072">
              <h2 className="text-3xl md:text-4xl font-medium mb-8" data-id="element-2073">
                Generate your child's map
              </h2>
              <p className="text-lg text-[#F5F5F5]/70 mb-12 max-w-2xl mx-auto" data-id="element-2074">
                Enter your child's birth data to generate their Child System
                Map. You'll see a preview before deciding to unlock the full
                map.
              </p>
              <button onClick={handleGenerateDiagnostic} className="inline-block px-12 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-2075">
                Generate Map →
              </button>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="py-8 px-6 border-t border-[#2B2B2B]" data-id="element-2076">
          <div className="max-w-7xl mx-auto text-center" data-id="element-2077">
            <p className="text-xs text-[#F5F5F5]/40" data-id="element-2078">
              © {new Date().getFullYear()} Lazy Monk Labs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}